import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import { InputNumber } from "@/components/InputNumber";
import Ellipsis from "@/components/views/Ellipsis";
import { datePickerClearableProps, datePickerFormItemProps, selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError } from "@/utils/formUtils";
import { isDefined, toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Card, Checkbox, Col, DatePicker, Form, Input, Row, Select } from "antd";
import { Rule } from "antd/es/form";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { Dayjs } from "dayjs";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../../common/constants";
import { FieldConstraintViolation } from "../../../../../../../common/types";
import ClientDrawerForm from "../../../../../../client/components/drawers/ClientDrawerForm";
import ClientSearchInput from "../../../../../../client/components/search/ClientSearchInput";
import { ClientFormStage, ClientFormType, ClientSearchActionType, ClientType } from "../../../../../../client/enums";
import { useClientSearch } from "../../../../../../client/hooks";
import { Client, NaturalClient } from "../../../../../../client/types";
import { CalcType } from "../../../../../enums";
import {
  HolderAccidentsYearsKey,
  TimeWithoutAccident,
  VehicleOwnerRelation,
  VehiclePolicyHolderRelation,
} from "../../../enums";
import { VehicleCalcForm, VehicleCalcFormClientsData } from "../../../types";
import { resolveGapCoverageLimitOptions, YEARS_TO_ACCIDENT_TIMES_MAP } from "../../../utils";

const getRegistrationDataRules = (firstRegistrationDate?: string, effectiveBeginningDate?: string) => {
  const registrationDateRules: Rule[] = [];

  if (firstRegistrationDate) {
    registrationDateRules.push(
      validations.notBefore(firstRegistrationDate, t("calc.vehicle.attrs.vehicleData.firstRegistrationDate")),
    );
  }
  if (effectiveBeginningDate) {
    registrationDateRules.push(
      validations.notAfter(effectiveBeginningDate, t("calc.vehicle.attrs.generalData.effectiveBeginningDate")),
    );
  }

  return registrationDateRules;
};

const colSpan = 4;
const eightColumnColSpan = 3;

interface Props {
  holder?: Partial<Client>;
  clientsViolationErrors: Map<ClientFormType, FieldConstraintViolation[]>;
  onHolderChange: (client?: Partial<Client>) => void;
  onHolderViolationErrorsDelete: () => void;
}

export const VehicleCalcClientsDataSection = ({ holder, clientsViolationErrors, ...props }: Props) => {
  const { t } = useTranslation();

  const clientSearch = useClientSearch();

  const [clientFormOpen, setClientFormOpen] = useState<boolean>(false);
  const [storedHolderFormStage, setStoredHolderFormStage] = useState<ClientFormStage | undefined>(
    holder ? ClientFormStage.SELECTED : undefined,
  );
  // Only the NEW stage (searched, but no such client exists) applies while there is no holder.
  const holderFormStage = holder || storedHolderFormStage === ClientFormStage.NEW ? storedHolderFormStage : undefined;

  const form = Form.useFormInstance<VehicleCalcForm>();
  const disabledPersonChecked = Form.useWatch(["clientsData", "holderIsDisabledPerson"], form);
  const firstRegistrationDate = Form.useWatch(["vehicleData", "firstRegistrationDate"], form);
  const effectiveBeginningDate = Form.useWatch(["generalData", "effectiveBeginningDate"], form);
  const holderIdentifier = Form.useWatch(["clientsData", "holderIdentifier"], form);
  const relationSelectsDisabled = Form.useWatch(["clientsData", "leasing"], form);
  const holderTimeWithoutAccident = Form.useWatch(["clientsData", "holderTimeWithoutAccident"], form);
  const holderAccidentsIn2Years = Form.useWatch(["clientsData", "holderAccidentsIn2Years"], form);
  const holderAccidentsIn3Years = Form.useWatch(["clientsData", "holderAccidentsIn3Years"], form);
  const holderAccidentsIn5Years = Form.useWatch(["clientsData", "holderAccidentsIn5Years"], form);
  const holderAccidentsIn8Years = Form.useWatch(["clientsData", "holderAccidentsIn8Years"], form);
  const holderAccidentsIn10Years = Form.useWatch(["clientsData", "holderAccidentsIn10Years"], form);

  const registrationDateRules = getRegistrationDataRules(firstRegistrationDate, effectiveBeginningDate);

  useEffect(() => {
    if (!clientSearch.result.keyword || clientSearch.result.keyword !== holderIdentifier) {
      return;
    }

    if (!clientSearch.result.data) {
      setStoredHolderFormStage(ClientFormStage.NEW);
      return;
    }

    setClientFormOpen(true);
    setStoredHolderFormStage(ClientFormStage.EXISTING);

    if (
      clientSearch.result.data.type === ClientType.NATURAL ||
      clientSearch.result.data.type === ClientType.SELF_EMPLOYED
    ) {
      props.onHolderChange({
        ...clientSearch.result.data,
        previousIdentityCardNumber: (clientSearch.result.data as NaturalClient).identityCardNumber,
      });
    } else {
      props.onHolderChange(clientSearch.result.data);
    }
  }, [clientSearch.result, holderIdentifier]);

  useEffect(() => {
    return () => {
      clientSearch.onResultDelete();
    };
  }, []);

  const handleLeasingChange = (event: CheckboxChangeEvent) => {
    if (event.target.checked) {
      form.setFieldsValue({
        clientsData: {
          policyHolderRelation: VehiclePolicyHolderRelation.SAME_AS_VEHICLE_HOLDER,
          ownerRelation: VehicleOwnerRelation.DIFFERENT_FROM_BOTH,
        },
      });
    }
  };

  const handleHolderTimeWithoutAccidentChange = (holderTimeWithoutAccident: TimeWithoutAccident) => {
    const clientsData = form.getFieldValue(["clientsData"]) as VehicleCalcFormClientsData;

    form.setFieldsValue({
      clientsData: {
        holderAccidentsIn2Years: (
          YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.TWO) as TimeWithoutAccident[]
        ).includes(holderTimeWithoutAccident)
          ? clientsData.holderAccidentsIn2Years || 1
          : 0,
        holderAccidentsIn3Years: (
          YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.THREE) as TimeWithoutAccident[]
        ).includes(holderTimeWithoutAccident)
          ? clientsData.holderAccidentsIn3Years || 1
          : 0,
        holderAccidentsIn5Years: (
          YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.FIVE) as TimeWithoutAccident[]
        ).includes(holderTimeWithoutAccident)
          ? clientsData.holderAccidentsIn5Years || 1
          : 0,
        holderAccidentsIn8Years: (
          YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.EIGHT) as TimeWithoutAccident[]
        ).includes(holderTimeWithoutAccident)
          ? clientsData.holderAccidentsIn8Years || 1
          : 0,
        holderAccidentsIn10Years: (
          YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.TEN) as TimeWithoutAccident[]
        ).includes(holderTimeWithoutAccident)
          ? clientsData.holderAccidentsIn10Years || 1
          : 0,
        holderAccidentsInAllYears: (
          YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.ALL) as TimeWithoutAccident[]
        ).includes(holderTimeWithoutAccident)
          ? clientsData.holderAccidentsInAllYears || 1
          : 0,
      },
    });
  };

  const handleHolderAccidentsInYearsChange = (accidents: number, years: number) => {
    const {
      holderTimeWithoutAccident,
      holderAccidentsIn2Years,
      holderAccidentsIn3Years,
      holderAccidentsIn5Years,
      holderAccidentsIn8Years,
      holderAccidentsIn10Years,
      holderAccidentsInAllYears,
    } = form.getFieldValue(["clientsData"]) as VehicleCalcFormClientsData;

    form.setFieldsValue({
      clientsData: {
        holderAccidentsIn2Years:
          years < 2 &&
          holderAccidentsIn2Years &&
          accidents > holderAccidentsIn2Years &&
          (YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.TWO) as TimeWithoutAccident[]).includes(
            holderTimeWithoutAccident,
          )
            ? accidents
            : holderAccidentsIn2Years,
        holderAccidentsIn3Years:
          years < 3 &&
          holderAccidentsIn3Years &&
          accidents > holderAccidentsIn3Years &&
          (YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.THREE) as TimeWithoutAccident[]).includes(
            holderTimeWithoutAccident,
          )
            ? accidents
            : holderAccidentsIn3Years,
        holderAccidentsIn5Years:
          years < 5 &&
          holderAccidentsIn5Years &&
          accidents > holderAccidentsIn5Years &&
          (YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.FIVE) as TimeWithoutAccident[]).includes(
            holderTimeWithoutAccident,
          )
            ? accidents
            : holderAccidentsIn5Years,
        holderAccidentsIn8Years:
          years < 8 &&
          holderAccidentsIn8Years &&
          accidents > holderAccidentsIn8Years &&
          (YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.EIGHT) as TimeWithoutAccident[]).includes(
            holderTimeWithoutAccident,
          )
            ? accidents
            : holderAccidentsIn8Years,
        holderAccidentsIn10Years:
          years < 10 &&
          holderAccidentsIn10Years &&
          accidents > holderAccidentsIn10Years &&
          (YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.TEN) as TimeWithoutAccident[]).includes(
            holderTimeWithoutAccident,
          )
            ? accidents
            : holderAccidentsIn10Years,
        holderAccidentsInAllYears:
          years <= 10 &&
          holderAccidentsInAllYears &&
          accidents > holderAccidentsInAllYears &&
          (YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.ALL) as TimeWithoutAccident[]).includes(
            holderTimeWithoutAccident,
          )
            ? accidents
            : holderAccidentsInAllYears,
      },
    });
  };

  const handleHolderRegistrationDateChange = (holderRegistrationDate: Dayjs | null) => {
    const gapCoverageLimit = form.getFieldValue(["generalData", "gapCoverageLimit"]);

    if (
      gapCoverageLimit &&
      holderRegistrationDate &&
      !resolveGapCoverageLimitOptions(holderRegistrationDate).includes(gapCoverageLimit)
    ) {
      form.setFieldsValue({ generalData: { gapCoverageLimit: undefined } });
    }
  };

  const handleClientSearchActionClick = (_: ClientFormType, actionType: ClientSearchActionType) => {
    switch (actionType) {
      case ClientSearchActionType.CREATE:
      case ClientSearchActionType.UPDATE:
        setClientFormOpen(true);
        break;
      case ClientSearchActionType.DELETE:
        form.setFieldsValue({ clientsData: { holderIdentifier: undefined } });
        setStoredHolderFormStage(undefined);
        props.onHolderViolationErrorsDelete();
        props.onHolderChange(undefined);
        break;
    }
  };

  const handleClientSearchSubmit = (value: string) => {
    form
      .validateFields([["clientsData", "holderIdentifier"]])
      .then(() => clientSearch.onSearch({ keyword: value }))
      .catch(resolveFormValidationError);
  };

  const handleClientSearchChange = (value: string) => {
    if (storedHolderFormStage) {
      setStoredHolderFormStage(undefined);
    }

    if (holder) {
      props.onHolderChange(undefined);
    }

    if (clientsViolationErrors.has(ClientFormType.HOLDER)) {
      props.onHolderViolationErrorsDelete();
    }

    handleClientSearchSubmit(value);
  };

  const handleClientFormSubmit = (client: Client) => {
    setClientFormOpen(false);
    setStoredHolderFormStage(ClientFormStage.SELECTED);
    props.onHolderViolationErrorsDelete();
    props.onHolderChange(client);
  };

  return (
    <>
      <Card type="inner" className="card-box" title={t("calc.vehicle.sections.vehicleHolder")}>
        <Row gutter={rowGutter}>
          <ClientSearchInput<ClientFormType>
            formItemProps={{
              name: ["clientsData", "holderIdentifier"],
              label: t("calc.vehicle.attrs.clientsData.holderIdentifier"),
              rules: [validations.notBlank, validations.pinOrCrn],
              validateFirst: true,
            }}
            formStage={holderFormStage}
            formType={ClientFormType.HOLDER}
            processedType={ClientFormType.HOLDER}
            client={holder}
            violationErrors={clientsViolationErrors}
            inProgress={clientSearch.inProgress}
            inputColSpan={colSpan + 1}
            clientNameColSpan={colSpan + 3}
            onActionClick={handleClientSearchActionClick}
            onSearch={handleClientSearchSubmit}
            onChange={handleClientSearchChange}
          />
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name={["clientsData", "holderRegistrationDate"]}
              label={t("calc.vehicle.attrs.clientsData.holderRegistrationDate")}
              rules={registrationDateRules.length > 0 ? registrationDateRules : [validations.none]}
              {...datePickerFormItemProps}
            >
              <DatePicker
                {...datePickerClearableProps}
                minDate={toDate(firstRegistrationDate)}
                maxDate={toDate(effectiveBeginningDate)}
                onChange={handleHolderRegistrationDateChange}
              />
            </Form.Item>
          </Col>

          <Col span={5}>
            <Form.Item
              name={["clientsData", "holderVehicleUsedByYoungRider"]}
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox style={{ maxHeight: 22 }}>
                <LabelWithPopover
                  label={t("calc.vehicle.attrs.clientsData.holderVehicleUsedByYoungRider")}
                  popoverContent={
                    <div style={{ width: "500px" }}>
                      <p>{t("calc.vehicle.helpers.holderVehicleUsedByYoungRiderDesc.question")}</p>
                      <ul>
                        <li>{t("calc.vehicle.helpers.holderVehicleUsedByYoungRiderDesc.bullet1")}</li>
                        <li>{t("calc.vehicle.helpers.holderVehicleUsedByYoungRiderDesc.bullet2")}</li>
                      </ul>
                    </div>
                  }
                />
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["clientsData", "holderIsDisabledPerson"]}
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox disabled={holder?.type !== ClientType.NATURAL}>
                {t("calc.vehicle.attrs.clientsData.holderIsDisabledPerson")}
              </Checkbox>
            </Form.Item>
          </Col>

          {disabledPersonChecked && (
            <Col span={colSpan}>
              <Form.Item
                name={["clientsData", "disabledPersonIdCardNumber"]}
                label={t("calc.vehicle.attrs.clientsData.disabledPersonIdCardNumber")}
                rules={[validations.numeric, validations.length(6)]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name={["clientsData", "policyHolderRelation"]}
              label={t("calc.vehicle.enums.vehiclePolicyHolderRelation._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                disabled={relationSelectsDisabled}
                options={Object.keys(VehiclePolicyHolderRelation).map((relation) => ({
                  value: relation,
                  label: t("calc.vehicle.enums.vehiclePolicyHolderRelation." + relation),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["clientsData", "ownerRelation"]}
              label={t("calc.vehicle.enums.vehicleOwnerRelation._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                disabled={relationSelectsDisabled}
                options={Object.keys(VehicleOwnerRelation).map((relation) => ({
                  value: relation,
                  label: t("calc.vehicle.enums.vehicleOwnerRelation." + relation),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={5}>
            <Form.Item
              name={["clientsData", "leasing"]}
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox onChange={handleLeasingChange}>{t("calc.vehicle.attrs.clientsData.leasing")}</Checkbox>
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["clientsData", "holderHasNeverBeenMtplInsured"]}
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox style={{ maxHeight: 22 }}>
                {t("calc.vehicle.attrs.clientsData.holderHasNeverBeenMtplInsured")}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.type !== next.type}>
              {({ getFieldValue }) => (
                <Form.Item
                  name={["clientsData", "holderHasNeverBeenCrashInsured"]}
                  className="form-item-without-label"
                  valuePropName="checked"
                  rules={[validations.none]}
                  initialValue={false}
                >
                  <Checkbox style={{ maxHeight: 22 }} disabled={getFieldValue(["type"]) === CalcType.MTPL}>
                    {t("calc.vehicle.attrs.clientsData.holderHasNeverBeenCrashInsured")}
                  </Checkbox>
                </Form.Item>
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name={["clientsData", "holderTimeWithoutAccident"]}
              label={<Ellipsis>{t("calc.vehicle.enums.timeWithoutAccident._label")}</Ellipsis>}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.keys(TimeWithoutAccident).map((time) => ({
                  value: time,
                  label: t("calc.vehicle.enums.timeWithoutAccident." + time),
                }))}
                onChange={handleHolderTimeWithoutAccidentChange}
              />
            </Form.Item>
          </Col>

          {YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.TWO)?.includes(holderTimeWithoutAccident) && (
            <Col span={eightColumnColSpan}>
              <Form.Item
                name={["clientsData", "holderAccidentsIn2Years"]}
                label={t("calc.vehicle.attrs.clientsData.holderAccidentsIn2Years")}
                rules={[validations.notNull, validations.minNumber(1)]}
              >
                <InputNumber onChange={(value) => (value ? handleHolderAccidentsInYearsChange(value, 2) : undefined)} />
              </Form.Item>
            </Col>
          )}

          {YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.THREE)?.includes(holderTimeWithoutAccident) && (
            <Col span={eightColumnColSpan}>
              <Form.Item
                name={["clientsData", "holderAccidentsIn3Years"]}
                label={t("calc.vehicle.attrs.clientsData.holderAccidentsIn3Years")}
                rules={[
                  validations.notNull,
                  validations.minNumber(
                    isDefined(holderAccidentsIn2Years) && holderAccidentsIn2Years > 0 ? holderAccidentsIn2Years : 1,
                    isDefined(holderAccidentsIn2Years) && holderAccidentsIn2Years > 0
                      ? t("calc.vehicle.attrs.clientsData.holderAccidentsIn2Years")
                      : undefined,
                  ),
                ]}
              >
                <InputNumber onChange={(value) => (value ? handleHolderAccidentsInYearsChange(value, 3) : undefined)} />
              </Form.Item>
            </Col>
          )}

          {YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.FIVE)?.includes(holderTimeWithoutAccident) && (
            <Col span={eightColumnColSpan}>
              <Form.Item
                name={["clientsData", "holderAccidentsIn5Years"]}
                label={t("calc.vehicle.attrs.clientsData.holderAccidentsIn5Years")}
                rules={[
                  validations.notNull,
                  validations.minNumber(
                    isDefined(holderAccidentsIn3Years) && holderAccidentsIn3Years > 0 ? holderAccidentsIn3Years : 1,
                    isDefined(holderAccidentsIn3Years) && holderAccidentsIn3Years > 0
                      ? t("calc.vehicle.attrs.clientsData.holderAccidentsIn3Years")
                      : undefined,
                  ),
                ]}
              >
                <InputNumber onChange={(value) => (value ? handleHolderAccidentsInYearsChange(value, 5) : undefined)} />
              </Form.Item>
            </Col>
          )}

          {YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.EIGHT)?.includes(holderTimeWithoutAccident) && (
            <Col span={eightColumnColSpan}>
              <Form.Item
                name={["clientsData", "holderAccidentsIn8Years"]}
                label={t("calc.vehicle.attrs.clientsData.holderAccidentsIn8Years")}
                rules={[
                  validations.notNull,
                  validations.minNumber(
                    isDefined(holderAccidentsIn5Years) && holderAccidentsIn5Years > 0 ? holderAccidentsIn5Years : 1,
                    isDefined(holderAccidentsIn5Years) && holderAccidentsIn5Years > 0
                      ? t("calc.vehicle.attrs.clientsData.holderAccidentsIn5Years")
                      : undefined,
                  ),
                ]}
              >
                <InputNumber onChange={(value) => (value ? handleHolderAccidentsInYearsChange(value, 8) : undefined)} />
              </Form.Item>
            </Col>
          )}

          {YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.TEN)?.includes(holderTimeWithoutAccident) && (
            <Col span={eightColumnColSpan}>
              <Form.Item
                name={["clientsData", "holderAccidentsIn10Years"]}
                label={t("calc.vehicle.attrs.clientsData.holderAccidentsIn10Years")}
                rules={[
                  validations.notNull,
                  validations.minNumber(
                    isDefined(holderAccidentsIn8Years) && holderAccidentsIn8Years > 0 ? holderAccidentsIn8Years : 1,
                    isDefined(holderAccidentsIn8Years) && holderAccidentsIn8Years > 0
                      ? t("calc.vehicle.attrs.clientsData.holderAccidentsIn8Years")
                      : undefined,
                  ),
                ]}
              >
                <InputNumber
                  onChange={(value) => (value ? handleHolderAccidentsInYearsChange(value, 10) : undefined)}
                />
              </Form.Item>
            </Col>
          )}

          {YEARS_TO_ACCIDENT_TIMES_MAP.get(HolderAccidentsYearsKey.ALL)?.includes(holderTimeWithoutAccident) && (
            <Col span={eightColumnColSpan}>
              <Form.Item
                name={["clientsData", "holderAccidentsInAllYears"]}
                label={t("calc.vehicle.attrs.clientsData.holderAccidentsInAllYears")}
                rules={[
                  validations.notNull,
                  validations.minNumber(
                    isDefined(holderAccidentsIn10Years) && holderAccidentsIn10Years > 0 ? holderAccidentsIn10Years : 1,
                    isDefined(holderAccidentsIn10Years) && holderAccidentsIn10Years > 0
                      ? t("calc.vehicle.attrs.clientsData.holderAccidentsIn10Years")
                      : undefined,
                  ),
                ]}
              >
                <InputNumber />
              </Form.Item>
            </Col>
          )}
        </Row>

        {isDefined(holderAccidentsIn3Years) && holderAccidentsIn3Years >= 3 && (
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["clientsData", "currentlyInsuredVehicles"]}
                label={t("calc.vehicle.attrs.clientsData.currentlyInsuredVehicles")}
                rules={[validations.minNumber(0)]}
              >
                <InputNumber />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Card>

      <ClientDrawerForm<ClientFormType>
        open={clientFormOpen}
        client={holder}
        initialIdentifier={holderIdentifier}
        formType={ClientFormType.HOLDER}
        violationErrors={clientsViolationErrors}
        onFormSubmit={handleClientFormSubmit}
      />
    </>
  );
};
